// src/components/Booking/Booking.js

import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import './Booking.css';
import { useSearchParams } from 'react-router-dom';
import OtpModal from './OtpModal';

// --- DATA: (Indian States data remains unchanged) ---
const indianStates = { "Andhra Pradesh": ["Visakhapatnam", "Vijayawada", "Guntur", "Tirupati", "Kakinada"], "Arunachal Pradesh": ["Itanagar", "Tawang", "Naharlagun"], "Assam": ["Guwahati", "Dibrugarh", "Silchar", "Jorhat"], "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur"], "Chhattisgarh": ["Raipur", "Bhilai", "Bilaspur", "Korba"], "Goa": ["Panaji", "Vasco da Gama", "Margao"], "Gujarat": ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Gandhinagar"], "Haryana": ["Faridabad", "Gurugram", "Panipat", "Ambala", "Chandigarh"], "Himachal Pradesh": ["Shimla", "Manali", "Dharamshala", "Solan"], "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro Steel City"], "Karnataka": ["Bengaluru", "Mysuru", "Mangaluru", "Hubli-Dharwad", "Belagavi"], "Kerala": ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur"], "Madhya Pradesh": ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain"], "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Nashik", "Thane"], "Manipur": ["Imphal", "Thoubal"], "Meghalaya": ["Shillong", "Tura"], "Mizoram": ["Aizawl", "Lunglei"], "Nagaland": ["Kohima", "Dimapur"], "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela", "Puri"], "Punjab": ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Chandigarh"], "Rajasthan": ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Bikaner"], "Sikkim": ["Gangtok", "Namchi"], "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem"], "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar"], "Tripura": ["Agartala", "Udaipur (Tripura)"], "Uttar Pradesh": ["Lucknow", "Kanpur", "Ghaziabad", "Agra", "Varanasi", "Meerut", "Noida"], "Uttarakhand": ["Dehradun", "Haridwar", "Rishikesh", "Nainital"], "West Bengal": ["Kolkata", "Howrah", "Durgapur", "Siliguri", "Asansol"], "Andaman and Nicobar Islands": ["Port Blair"], "Chandigarh": ["Chandigarh"], "Dadra and Nagar Haveli and Daman and Diu": ["Daman", "Silvassa"], "Delhi": ["New Delhi", "North Delhi", "South Delhi", "West Delhi", "East Delhi"], "Jammu and Kashmir": ["Srinagar", "Jammu", "Anantnag"], "Ladakh": ["Leh", "Kargil"], "Lakshadweep": ["Kavaratti"], "Puducherry": ["Puducherry", "Karaikal"]};

// --- SVG Icons (Unchanged) ---
const DropdownArrowIcon = () => ( <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-arrow-icon"><path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> );
const RefreshIcon = ({ className, onClick }) => ( <svg className={className} onClick={onClick} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg> );
const CheckmarkIcon = ({ className }) => (<svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>);

const Mandatory = () => <span className="mandatory-asterisk">*</span>;
const REACT_APP_TEST = process.env.REACT_APP_TEST || 'https://api.nownfc.com:5007';

export default function Booking() {
    const [searchParams] = useSearchParams();
    
    // --- NEW: State for verification and user session ---
    const [isVerified, setIsVerified] = useState(false);
    const [authenticatedUser, setAuthenticatedUser] = useState(null);
    const [isOtpModalOpen, setIsOtpModalOpen] = useState(true); // Control modal visibility

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', contactNumber: '', email: '',
        state: '', city: '', address: '', quantity: '1', color: '',
        companyName: '', designation: '', userCaptcha: '',
        cardType: 'pvc', 
        planName: '',
    });
    const [pricing, setPricing] = useState({
        basePrice: 0,
        subtotal: 0,
        gst: 0,
        total: 0
    });

    const [selectedCard, setSelectedCard] = useState('black');
    const [selectedFinish, setSelectedFinish] = useState('matte');
    const [filteredCities, setFilteredCities] = useState([]);
    const [isMounted, setIsMounted] = useState(false);
    const [showCompanyDetails, setShowCompanyDetails] = useState(false);
    const [captchaText, setCaptchaText] = useState('');

    // --- NEW: Check for a valid session on component mount ---
    useEffect(() => {
        const checkSession = () => {
            const storedSession = localStorage.getItem('userSession');
            if (storedSession) {
                const sessionData = JSON.parse(storedSession);
                const { user, verifiedAt } = sessionData;
                const fifteenMinutes = 15 * 60 * 1000;
                const isSessionValid = (Date.now() - verifiedAt) < fifteenMinutes;

                if (isSessionValid && user) {
                    console.log("Active session found. Bypassing OTP verification.");
                    setAuthenticatedUser(user);
                    setIsVerified(true);
                    setIsOtpModalOpen(false); // Don't show the modal
                } else {
                    console.log("Session expired or invalid. Clearing localStorage.");
                    localStorage.removeItem('userSession');
                }
            }
        };
        checkSession();
    }, []);

    // --- NEW: Pre-fill form when user is authenticated ---
    useEffect(() => {
        if (authenticatedUser) {
            const nameParts = authenticatedUser.name.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            setFormData(prev => ({
                ...prev,
                firstName,
                lastName,
                contactNumber: authenticatedUser.phone,
            }));
        }
    }, [authenticatedUser]);

    // --- NEW: Handle successful OTP verification from modal ---
    const handleVerificationSuccess = (user) => {
        setAuthenticatedUser(user);
        setIsVerified(true);
        setIsOtpModalOpen(false); // Close the modal on success
    };

    // --- Existing Effects (Unchanged) ---
    useEffect(() => {
        const plan = searchParams.get('plan');
        const price = searchParams.get('price');

        if (plan && price) {
            setFormData(prev => ({
                ...prev,
                planName: plan,
                cardType: plan.toLowerCase().includes('metal') ? 'metal' : 'pvc',
            }));
            setPricing(prev => ({ ...prev, basePrice: Number(price) }));
        }
    }, [searchParams]);

    useEffect(() => {
        const quantity = Number(formData.quantity) || 0;
        const subtotal = pricing.basePrice * quantity;
        const gstAmount = subtotal * 0.18;
        const totalAmount = subtotal + gstAmount;
        setPricing(prev => ({ ...prev, subtotal, gst: gstAmount, total: totalAmount }));
    }, [formData.quantity, pricing.basePrice]);

    const generateCaptcha = useCallback(() => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let c = '';
        for (let i = 0; i < 6; i++) c += chars.charAt(Math.floor(Math.random() * chars.length));
        setCaptchaText(c);
    }, []);

    useEffect(() => {
        generateCaptcha();
        setIsMounted(true);
    }, [generateCaptcha]);

    useEffect(() => {
        if (formData.state) {
            setFilteredCities(indianStates[formData.state] || []);
            setFormData(prev => ({ ...prev, city: '' }));
        } else {
            setFilteredCities([]);
        }
    }, [formData.state]);

    // --- Event Handlers (Unchanged, but contactNumber handler is now less critical) ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'quantity') {
            const numValue = Number(value);
            if (numValue > 25) {
                Swal.fire({ icon: 'warning', title: 'Bulk Orders', text: 'For orders above 25 cards, please contact us directly.', confirmButtonColor: '#007bff'});
                setFormData(prev => ({ ...prev, [name]: '25' }));
                return;
            }
        }
        if (name === 'contactNumber') { // Still useful if session expires
            const numericValue = value.replace(/[^0-9]/g, '');
            if (numericValue.length <= 10) setFormData(prev => ({ ...prev, [name]: numericValue }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleRefreshCaptcha = () => {
        generateCaptcha();
        setFormData(p => ({ ...p, userCaptcha: '' }));
    };
    
    // --- Form Validation & Submission (Unchanged) ---
    const validateForm = () => {
        const { firstName, lastName, contactNumber, email, state, city, address, userCaptcha } = formData;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!firstName || !lastName || !contactNumber || !email || !state || !city || !address) {
            Swal.fire('Error', 'Please fill all mandatory fields.', 'error'); return false;
        }
        if (contactNumber.length !== 10) {
            Swal.fire('Error', 'Please enter a valid 10-digit contact number.', 'error'); return false;
        }
        if (!emailRegex.test(email)) {
            Swal.fire('Error', 'Please enter a valid email address.', 'error'); return false;
        }
        if (userCaptcha.toUpperCase() !== captchaText.toUpperCase()) {
            Swal.fire('Error', 'The security check text does not match. Please try again.', 'error');
            handleRefreshCaptcha();
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        const orderData = { ...formData, selectedCard, selectedFinish, totalAmount: pricing.total.toFixed(2) };

        try {
            const response = await fetch(`${REACT_APP_TEST}/api/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderData)
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const result = await response.json();
            console.log("Order submitted successfully:", result);
            Swal.fire({ icon: 'success', title: 'Order Placed!', text: "We've received your order and will be in touch shortly.", timer: 5000, timerProgressBar: true });
            setFormData({
                firstName: '', lastName: '', contactNumber: '', email: '', state: '', city: '', address: '', quantity: '1',
                color: '', companyName: '', designation: '', cardType: 'pvc', userCaptcha: '', planName: ''
            });
            setSelectedCard('black');
            setSelectedFinish('matte');
            handleRefreshCaptcha();
        } catch (error) {
            console.error("Submission Error:", error);
            Swal.fire({ icon: 'error', title: 'Submission Failed', text: 'There was a problem submitting your order.' });
        }
    };

    // --- Card Interaction (Unchanged) ---
    const handleMouseMove = (e) => {
        const card = e.currentTarget;
        const { left, top, width, height } = card.getBoundingClientRect();
        const x = e.clientX - left; const y = e.clientY - top;
        const rotateX = -10 * ((y - height / 2) / height);
        const rotateY = 10 * ((x - width / 2) / width);
        card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    };
    const handleMouseLeave = (e) => { e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg)'; };

    // --- Main Render ---
    if (!isVerified && isOtpModalOpen) {
        return <OtpModal 
            onVerificationSuccess={handleVerificationSuccess} 
            onClose={() => setIsOtpModalOpen(false)} // Allows modal to close itself
        />;
    }

    return (
        <div className="booking-fullscreen-container">
            <div className={`booking-visual-panel ${isMounted ? 'mounted' : ''}`}>
                <div className="booking-visual-header">
                    <h1>Your Next <span>Impression</span>, Delivered.</h1>
                    <p>Select your style and fill out the form to order the revolutionary Now NFC card. The future of networking is just a tap away.</p>
                </div>
                
                <div className="booking-card-preview-area">
                    <div className={`booking-nfc-card card-preview ${selectedCard}`} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                        <div className="card-chip"></div>
                        <div className={`card-logo ${selectedCard === 'black' ? 'text-light' : 'text-dark'}`}>Now NFC</div>
                    </div>
                </div>

                <div className="booking-card-options-container">
                    <div className="booking-card-options-grid">
                        {['black', 'golden', 'silver'].map(color => (
                            <div key={color} className={`booking-card-option ${selectedCard === color ? 'selected' : ''}`} onClick={() => setSelectedCard(color)}>
                                <div className={`booking-nfc-card ${color}`}>
                                    <div className="card-chip"></div>
                                    <div className={`card-logo ${color === 'black' ? 'text-light' : 'text-dark'}`}></div>
                                </div>
                                <div className="selected-checkmark"><CheckmarkIcon /></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className={`booking-form-panel ${isMounted ? 'mounted' : ''}`}>
                <form className="booking-order-form" onSubmit={handleSubmit} noValidate>
                    <h2 className="booking-form-title">Complete Your Order</h2>
                    
                    <div className="booking-form-grid">
                        <div className="booking-form-section-title grid-col-span-2">Verified Details</div>
                        
                        {/* --- NEW: Read-only pre-filled fields --- */}
                        <div className="form-group">
                            <input type="text" id="firstName" name="firstName" className="form-input" value={formData.firstName} readOnly />
                            <label htmlFor="firstName" className="form-label">First Name <Mandatory /></label>
                        </div>
                        <div className="form-group">
                            <input type="text" id="lastName" name="lastName" className="form-input" value={formData.lastName} readOnly />
                            <label htmlFor="lastName" className="form-label">Last Name <Mandatory /></label>
                        </div>
                        <div className="form-group grid-col-span-2">
                            <input type="tel" id="contactNumber" name="contactNumber" className="form-input" value={formData.contactNumber} readOnly />
                            <label htmlFor="contactNumber" className="form-label">Contact Number <Mandatory /></label>
                        </div>

                        <div className="booking-form-section-title grid-col-span-2">Shipping Details</div>

                        <div className="form-group grid-col-span-2">
                            <input type="email" id="email" name="email" className="form-input" placeholder=" " value={formData.email} onChange={handleInputChange} required />
                            <label htmlFor="email" className="form-label">Email Address <Mandatory /></label>
                        </div>
                        
                        <div className="form-group-top-label">
                            <label htmlFor="state" className="form-label-top">State <Mandatory /></label>
                            <div className="select-wrapper">
                                <select id="state" name="state" className="form-select" value={formData.state} onChange={handleInputChange} required>
                                    <option value="">Select State...</option>
                                    {Object.keys(indianStates).sort().map(state => <option key={state} value={state}>{state}</option>)}
                                </select>
                                <DropdownArrowIcon />
                            </div>
                        </div>

                        <div className="form-group-top-label">
                            <label htmlFor="city" className="form-label-top">City <Mandatory /></label>
                            <div className="select-wrapper">
                                <select id="city" name="city" className="form-select" value={formData.city} onChange={handleInputChange} required disabled={!formData.state}>
                                    <option value="">{formData.state ? 'Select City...' : 'Select state first'}</option>
                                    {filteredCities.map(city => <option key={city} value={city}>{city}</option>)}
                                </select>
                                <DropdownArrowIcon />
                            </div>
                        </div>

                        <div className="form-group grid-col-span-2">
                            <textarea id="address" name="address" className="form-textarea" placeholder=" " value={formData.address} onChange={handleInputChange} required></textarea>
                            <label htmlFor="address" className="form-label">Full Shipping Address <Mandatory /></label>
                        </div>
                        
                        <div className="booking-form-section-title grid-col-span-2">Professional Details (Optional)</div>
                        
                        <div className="form-group-checkbox grid-col-span-2">
                            <input type="checkbox" id="showCompanyDetails" className="custom-checkbox" checked={showCompanyDetails} onChange={e => setShowCompanyDetails(e.target.checked)} />
                            <label htmlFor="showCompanyDetails">Add company details for card personalization</label>
                        </div>

                        {showCompanyDetails && (
                            <>
                                <div className="form-group grid-col-span-2">
                                    <input type="text" id="companyName" name="companyName" className="form-input" placeholder=" " value={formData.companyName} onChange={handleInputChange} required={showCompanyDetails} />
                                    <label htmlFor="companyName" className="form-label">Company Name <Mandatory /></label>
                                </div>
                                <div className="form-group grid-col-span-2">
                                    <input type="text" id="designation" name="designation" className="form-input" placeholder=" " value={formData.designation} onChange={handleInputChange} required={showCompanyDetails} />
                                    <label htmlFor="designation" className="form-label">Designation / Title <Mandatory /></label>
                                </div>
                            </>
                        )}

                        <div className="booking-form-section-title grid-col-span-2">Card Customization</div>
                        
                        <div className="form-group">
                            <input type="number" id="quantity" name="quantity" className="form-input" placeholder=" " value={formData.quantity} onChange={handleInputChange} min="1" max="25" />
                            <label htmlFor="quantity" className="form-label">Quantity (Max 25)</label>
                        </div>
                        
                        <div className="form-group">
                            <input type="text" id="color" name="color" className="form-input" placeholder=" " value={formData.color} onChange={handleInputChange} />
                            <label htmlFor="color" className="form-label">Color / Logo Text</label>
                        </div>

                        <fieldset className="grid-col-span-2 booking-fieldset">
                            <legend className="booking-legend">Finish</legend>
                            <div className="finish-options">
                                {['matte', 'glossy', 'transparent'].map(finish => (
                                    <React.Fragment key={finish}>
                                        <input type="radio" id={finish} name="selectedFinish" value={finish} className="finish-radio-input" checked={selectedFinish === finish} onChange={() => setSelectedFinish(finish)} />
                                        <label htmlFor={finish} className="finish-radio-label">{finish.charAt(0).toUpperCase() + finish.slice(1)}</label>
                                    </React.Fragment>
                                ))}
                            </div>
                        </fieldset>

                        <fieldset className="grid-col-span-2 booking-fieldset">
                            <legend className="booking-legend">Card Type</legend>
                            <div className="finish-options">
                                {['pvc', 'metal'].map(type => (
                                    <React.Fragment key={type}>
                                        <input type="radio" id={type} name="cardType" value={type} className="finish-radio-input" checked={formData.cardType === type} onChange={handleInputChange} />
                                        <label htmlFor={type} className="finish-radio-label">{type.charAt(0).toUpperCase() + type.slice(1)}</label>
                                    </React.Fragment>
                                ))}
                            </div>
                        </fieldset>
                        
                        <div className="captcha-group grid-col-span-2">
                            <label htmlFor="userCaptcha" className="form-label-top">Security Check <Mandatory /></label>
                            <div className="captcha-container">
                                <div className="captcha-image-wrapper">
                                    <span className="captcha-text">{captchaText}</span>
                                    <RefreshIcon className="captcha-refresh-icon" onClick={handleRefreshCaptcha} />
                                </div>
                                <input type="text" id="userCaptcha" name="userCaptcha" value={formData.userCaptcha} onChange={handleInputChange} placeholder="Enter text here" required autoComplete="off" className="form-input"/>
                            </div>
                        </div>

                        <div className="booking-form-section-title grid-col-span-2">Payment Summary</div>
                        
                        <div className="pricing-summary grid-col-span-2">
                            <div className="summary-item">
                                <span>Subtotal (₹{pricing.basePrice.toFixed(2)} x {formData.quantity})</span>
                                <span>₹{pricing.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="summary-item">
                                <span>GST (18%)</span>
                                <span>+ ₹{pricing.gst.toFixed(2)}</span>
                            </div>
                            <div className="summary-total">
                                <span>Total Amount</span>
                                <span>₹{pricing.total.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="grid-col-span-2" style={{ marginTop: '1.5rem' }}>
                            <button type="submit" className="cta-button">Place Order</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}